import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@nerdwallet/apollo';
import _ from 'lodash';
import { connect } from 'react-redux';
import Login from '../../components/login/login';
import BrandedLogin from '../../components/branded-login/branded-login';
import { setAuthenticatedStatus } from './authentication-reducer';

export const IDENTITY_QUERY = gql`
  query Query {
    identity {
      id
      email
      roles
    }
  }
`;

const Authentication = ({
  requiresAuth,
  authRoleRequired,
  onSuccess,
  loginModalCopyOverride,
  loginPageCopyOverride,
  loginTrkLocation,
  loginTrkFormat,
  children,
  setAuthenticated,
  // branded Login
  useBrandedLogin,
  brandedBackgroundImage,
  brandedIcon,
  brandedTitle,
  brandedSubtitle,
  customCtaText,
  topLoginBoxColor,
  bottomLoginBoxColor,
  topLoginBoxMargin,
  bottomLoginBoxMargin,
  outputAnimatedArrowAboveCta,
  animatedImageUrl,
}) => {
  // Determining if a user is logged in or not
  const { data } = useQuery(IDENTITY_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const isEmployee = _.includes(data?.identity?.roles, 'nerdwallet/employee');
  const hasAuth =
    isEmployee || _.includes(data?.identity?.roles, `${authRoleRequired}`);
  const isSignedIn = data?.identity?.id;

  // in order to keep all the others components which rely on legacy
  // identity.isSignedIn working we are manually updating the identity object,
  // (which now is being feed by js-auth-tools which one will be retiring soon)
  // with the data we got from supergraph identity query.
  useEffect(() => {
    if (isSignedIn) {
      setAuthenticated({
        id: data?.identity?.id,
        roles: data?.identity?.roles,
      });
    }
  }, [data?.identity?.id, data?.identity?.roles, isSignedIn, setAuthenticated]);

  const LoginComponent = useBrandedLogin ? BrandedLogin : Login;

  return requiresAuth && !hasAuth ? (
    <LoginComponent
      onSuccess={onSuccess}
      isSignedIn={isSignedIn}
      loginModalCopyOverride={loginModalCopyOverride}
      loginPageCopyOverride={loginPageCopyOverride}
      loginTrkLocation={loginTrkLocation}
      loginTrkFormat={loginTrkFormat}
      // branded Login
      brandedBackgroundImage={brandedBackgroundImage}
      brandedIcon={brandedIcon}
      brandedTitle={brandedTitle}
      brandedSubtitle={brandedSubtitle}
      customCtaText={customCtaText}
      topLoginBoxColor={topLoginBoxColor}
      bottomLoginBoxColor={bottomLoginBoxColor}
      topLoginBoxMargin={topLoginBoxMargin}
      bottomLoginBoxMargin={bottomLoginBoxMargin}
      outputAnimatedArrowAboveCta={outputAnimatedArrowAboveCta}
      animatedImageUrl={animatedImageUrl}
    />
  ) : (
    children
  );
};

Authentication.propTypes = {
  requiresAuth: PropTypes.bool,
  authRoleRequired: PropTypes.string,
  onSuccess: PropTypes.string,
  loginModalCopyOverride: PropTypes.string,
  loginPageCopyOverride: PropTypes.string,
  loginTrkLocation: PropTypes.string,
  loginTrkFormat: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.array]),
  setAuthenticated: PropTypes.func,
  // branded Login
  useBrandedLogin: PropTypes.bool,
  brandedBackgroundImage: PropTypes.string,
  brandedIcon: PropTypes.string,
  brandedTitle: PropTypes.string,
  brandedSubtitle: PropTypes.string,
  customCtaText: PropTypes.string,
  topLoginBoxColor: PropTypes.string,
  bottomLoginBoxColor: PropTypes.string,
  topLoginBoxMargin: PropTypes.number,
  bottomLoginBoxMargin: PropTypes.number,
  outputAnimatedArrowAboveCta: PropTypes.bool,
  animatedImageUrl: PropTypes.string,
};

const mapDispatchToProps = (dispatch) => ({
  setAuthenticated: ({ id, roles }) => {
    dispatch(setAuthenticatedStatus({ id, roles }));
  },
});

const AuthenticationWrapped = connect(null, mapDispatchToProps)(Authentication);
AuthenticationWrapped.displayName = 'Wrapped Authentication';

export default AuthenticationWrapped;
